/**
 * 设备 WebSocket 连接管理类
 * 封装 WebSocket 连接、消息处理、桥接初始化等逻辑
 */
export class DeviceWebSocket {
  constructor(port = 11223, options = {}) {
    this.port = port;
    this.ws = null;
    this.eventHandlers = {};
    
    // 重连控制
    this.reconnectTimer = null;
    this.reconnectAttempts = 0;
    this.lastConnectTime = 0; // 最后连接成功时间,用于判断连接稳定性
    this.isManualClose = false;
    this.hasConnectedOnce = false; // 标记是否曾经连接成功过
    
    // 配置选项
    this.options = {
      reconnectPath: "/device-connect",
      maxReconnectAttempts: 5, // 持续重连 10 次，给 Driver 启动时间
      reconnectInterval: 1000, // 每 1 秒重连一次
      stableConnectionTime: 5000, // 连接持续5秒以上才算稳定
      testMode: false, // 测试模式：不创建真实 WebSocket，用于 DeviceCallbackTest
      ...options,
    };

    // 测试模式标记
    this.isTestMode = this.options.testMode;
    
    if (this.isTestMode) {
      console.log('%c[DeviceWebSocket] 测试模式已启用', 'color: #722ed1; font-weight: bold;');
    }
  }

  /**
   * 连接 WebSocket
   */
  connect() {
    // 测试模式：模拟连接成功
    if (this.isTestMode) {
      console.log('%c[DeviceWebSocket] 测试模式：模拟连接成功', 'color: #722ed1; font-weight: bold;');
      
      // 创建模拟的 WebSocket 对象
      this.ws = {
        readyState: 1, // WebSocket.OPEN
        send: (data) => {
          console.log('%c[WebSocket.send] 测试模式 - 模拟发送:', 'color: #722ed1; font-weight: bold;');
          try {
            // 尝试解析并美化打印
            const parsed = JSON.parse(data);
            console.log('%c  方法:', 'color: #722ed1;', parsed.method);
            console.log('%c  数据:', 'color: #722ed1;', parsed.data);
            console.log('%c  完整消息:', 'color: #722ed1;', data);
          } catch (e) {
            // 如果不是 JSON，直接打印
            console.log('%c  原始数据:', 'color: #722ed1;', data);
          }
        },
        close: () => {
          console.log('%c[DeviceWebSocket] 测试模式 - 模拟关闭', 'color: #722ed1;');
        }
      };
      
      this.lastConnectTime = Date.now();
      this.hasConnectedOnce = true;
      
      // 延迟触发连接成功事件
      setTimeout(() => {
        this.emit("connected");
        this.initBridge();
      }, 100);
      return;
    }

    // 避免重复连接
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
      return;
    }

    // 清理旧连接
    this.cleanupWebSocket();

    // 创建新连接
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && 
                     navigator.vendor === "Apple Computer, Inc.";
    const protocol = isSafari ? "wss" : "ws";
    const socketUrl = `${protocol}://127.0.0.1:${this.port}`;

    try {
      this.ws = new WebSocket(socketUrl);
      this.setupEventListeners();
    } catch (error) {
      console.error("WebSocket connection failed:", error);
      this.emit("error", error);
    }
  }

  /**
   * 清理 WebSocket 连接
   */
  cleanupWebSocket() {
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      
      // 只在非 CLOSED 状态时关闭连接,避免浏览器警告
      if (this.ws.readyState !== WebSocket.CLOSED) {
        try {
          this.ws.close();
        } catch (e) {
          // 忽略关闭错误
        }
      }
      this.ws = null;
    }
  }

  /**
   * 设置 WebSocket 事件监听器
   */
  setupEventListeners() {
    if (!this.ws) return;

    this.ws.addEventListener("open", () => {
      console.log("WebSocket connected");
      // 记录连接成功时间(用于判断连接稳定性)
      this.lastConnectTime = Date.now();
      this.hasConnectedOnce = true; // 标记已成功连接过
      this.emit("connected");
      this.initBridge();
    });

    this.ws.addEventListener("message", (event) => {
      this.handleMessage(event.data);
    });

    this.ws.addEventListener("close", (event) => {
      // 只在非首次连接或已成功连接过的情况下打印日志
      if (this.hasConnectedOnce || this.reconnectAttempts > 0) {
        console.log("WebSocket closed, code:", event.code);
      }
      this.emit("closed", event);
      
      // 判断是否为稳定连接:连接持续 > 5秒
      if (this.lastConnectTime > 0) {
        const connectionDuration = Date.now() - this.lastConnectTime;
        if (connectionDuration > this.options.stableConnectionTime) {
          console.log(`Stable connection lasted ${connectionDuration}ms, resetting reconnect count`);
          this.reconnectAttempts = 0;
        }
      }
      
      // 非手动关闭时的处理
      if (!this.isManualClose) {
        if (this.hasConnectedOnce) {
          // 曾经连接成功过,尝试重连
          this.handleReconnect();
        } else {
          // 首次连接失败
          if (this.reconnectAttempts === 0) {
            // 第一次失败：触发首次连接失败事件
            console.log("First connection attempt failed, emitting firstConnectionFailed event");
            this.emit("firstConnectionFailed");
          }
          // 继续尝试重连
          this.handleReconnect();
        }
      }
    });

    this.ws.addEventListener("error", (event) => {
      // 只在非首次连接或已成功连接过的情况下打印错误
      if (this.hasConnectedOnce || this.reconnectAttempts > 0) {
        console.error("WebSocket error:", event);
      }
      this.emit("error", event);
    });
  }

  /**
   * 处理接收到的消息
   */
  handleMessage(data) {
    if (!data) return;

    try {
      const message = JSON.parse(data);
      const { method, data: messageData } = message;

      // 清除离线检测定时器(收到任何消息说明设备在线)
      // 注：离线检测已移至 Electricity.vue 的 isErrorConnected 统一处理
      if (window.updateElectricityTimer) {
        clearTimeout(window.updateElectricityTimer);
      }

      // 触发对应方法的事件处理器
      this.emit(method, messageData);

      // WebSocket 内部事件,不需要调用全局回调
      const skipGlobalCallback = ['hello', 'connected', 'closed', 'error'];
      
      // 调用全局回调函数
      // - HostEvents.all 中的方法: 由 DeviceCallbackManager 注册(订阅模式分发)

      // 注意: 每个方法只会在 window 上调用一次,不会重复
      // DeviceCallbackManager 内部负责分发给多个订阅者
      if (!skipGlobalCallback.includes(method)) {
        if (window[method] && typeof window[method] === 'function') {
          window[method](JSON.stringify(messageData));
        }
        // 不显示警告 - 某些方法可能在特定模式下不需要回调
      }
    } catch (error) {
      console.error("Failed to parse message:", error);
    }
  }

  /**
   * 初始化桥接对象
   * 返回 bridge 对象供外部保存到 store
   */
  initBridge() {
    const bridgeMethods = [
      // 系统基础方法
      "getSystemInfo",
      "setCurrentDevice",
      "updateLoader",
      "restartLoader",
      "updateFirmware",
      "setLanguage",
      "openCommunity",
      
      // Dongle 相关方法
      "getDongle",
      "updateDongle",
      "pairDongle",
      
      // 固件和配置管理
      "loadFirmware",
      "loadJson",
      "downloadJSON",
      "syncMacroKey",
      "syncJSON",
      "resetJSON",
      "downloadFile",
      "transport_mode",
      
      // AM30 (AM INFINITY .100) 相关方法
      "initMouseData",
      "getElectricity",
      "resetMouse",
      "changeMouseKey",
      "getMouseDPI",
      "setMouseDPI",
      "getMouseInfo",
      "setMouseProfile",
      "setMouseSnedorAngle",
      "setMouseInfo",
      "getAM30ProfileKeySwitch",
      "setAM30ProfileKeySwitch",
      "getMouseMacro",
      "saveMouseMacro",
      "deleteMouseMacro",
      "setMouseMacro",
      "resetMagnetKey",
      "getMouseSet",
      "autoMouseUpdate",
      "localMouseUpdate",
      "getCurrentVer",
      "clearBlue",
      "loadMousejson",
      "saveMousejson",
      "getMDLight",
      "setMDLight",
      "setDPIButton",
      "getDPIButton",
      "setFPS",
      "getFPS",
      
      // AM35 (AM Infinity .97) 相关方法
      "getAM35AllData",
      "getAM35Version",
      "getAM35Battery",
      "checkDeviceStatus",
      "autoAM35update",
      "handleAM35update",
      "resetAM35",
      "loadAM35json",
      "saveAM35json",
      "setAM35Profile",
      "getAM35ProfileKeySwitch",
      "setAM35ProfileKeySwitch",
      "setAM35DPI",
      "setAM35DPIButton",
      "setAM35DPIStages",
      "setAM35reportRate",
      "setAM35Debounce",
      "setAM35MotionSync",
      "setAM35LOD",
      "setAM35AngleSnapping",
      "setAM35RippleCtrl",
      "setAM35FPS",
      "setAM35rotateAngle",
      "setSleepTime",
      "changeAM35MouseKey",
      "setAM35light",
      "setAM35lightRGB",
      "setAM35Dlight",
      "saveAM35macro",
    ];

    const bridge = {};
    bridgeMethods.forEach((method) => {
      bridge[method] = (json) => {
        // 检查连接状态（兼容测试模式和真实模式）
        const isOpen = this.ws && (this.ws.readyState === 1 || (typeof WebSocket !== 'undefined' && this.ws.readyState === WebSocket.OPEN));
        
        if (isOpen) {
          // 处理 data 参数：如果已经是字符串，先解析再使用，避免双重序列化
          let dataToSend = json;
          // if (typeof json === 'string') {
          //   try {
          //     const parsed = JSON.parse(json);
          //     // 只有解析结果是对象或数组时才使用解析结果
          //     // 这样可以避免将字符串 "123" 转换为数字 123
          //     if (typeof parsed === 'object' && parsed !== null) {
          //       dataToSend = parsed;
          //     } else {
          //       // 原始值（数字、布尔、null字符串）保持字符串形式
          //       dataToSend = json;
          //     }
          //   } catch (e) {
          //     // 解析失败，保持原样（可能是普通字符串）
          //     dataToSend = json;
          //   }
          // }
          
          // 统一序列化（字符串）
          const message = JSON.stringify({ method, data: dataToSend });
          // const message = `{"method":"${method}","data":"${dataToSend||null}"}`;
          // 打印日志
          if (this.isTestMode) {
            console.log('%c[Bridge] 测试模式 - 调用方法:', 'color: #722ed1;', method);
            console.log('%c  输入参数 (json):', 'color: #722ed1;', json);
            console.log('%c  处理后数据 (dataToSend):', 'color: #722ed1;', dataToSend);
            console.log('%c  最终消息 (message):', 'color: #722ed1;', message);
          } else {
            console.log("WS.send", message);
          }
          
          this.ws.send(message);
        } else {
          console.error(`WebSocket is not connected, cannot call ${method}`);
          // 触发未连接事件
          this.emit("notConnected", { method, redirectPath: this.options.reconnectPath });
        }
      };
    });

    this.emit("bridgeReady", bridge);
    return bridge;
  }

  /**
   * 发送消息
   */
  send(method, data = "") {
    const message = JSON.stringify({ method, data });
    
    // 测试模式：只打印日志
    if (this.isTestMode) {
      console.log('%c[DeviceWebSocket] 测试模式 - 模拟发送:', 'color: #722ed1;', message);
      return;
    }

    // 检查连接状态
    const isOpen = this.ws && (this.ws.readyState === 1 || (typeof WebSocket !== 'undefined' && this.ws.readyState === WebSocket.OPEN));
    
    if (!isOpen) {
      console.error("WebSocket is not connected");
      return;
    }

    console.log("WS.send", message);
    this.ws.send(message);
  }

  /**
   * 处理自动重连
   */
  handleReconnect() {
    // 避免重复重连
    if (this.reconnectTimer) {
      return;
    }

    // 检查重连次数
    if (this.reconnectAttempts >= this.options.maxReconnectAttempts) {
      console.log("Max reconnection attempts reached");
      this.emit("maxReconnectReached");
      return;
    }

    this.reconnectAttempts++;
    console.log(`Reconnecting... Attempt ${this.reconnectAttempts}/${this.options.maxReconnectAttempts}`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, this.options.reconnectInterval);
  }

  /**
   * 关闭连接
   */
  close() {
    this.isManualClose = true;
    
    // 清理定时器
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // 测试模式：只清理事件处理器
    if (this.isTestMode) {
      this.ws = null;
      this.eventHandlers = {};
      console.log('%c[DeviceWebSocket] 测试模式：连接已关闭', 'color: #722ed1;');
      return;
    }

    // 清理连接
    this.cleanupWebSocket();
    this.eventHandlers = {};
  }

  /**
   * 模拟接收 WebSocket 消息（仅测试模式）
   * @param {string} method - 方法名
   * @param {object} data - 消息数据
   */
  simulateReceive(method, data) {
    if (!this.isTestMode) {
      console.error('[DeviceWebSocket] simulateReceive() 只能在测试模式下使用');
      return;
    }

    const message = JSON.stringify({ method, data });
    console.log('%c[DeviceWebSocket] 测试模式 - 模拟接收消息:', 'color: #722ed1; font-weight: bold;', message);
    
    this.handleMessage(message);
  }

  /**
   * 注册事件处理器
   */
  on(event, handler) {
    if (!this.eventHandlers[event]) {
      this.eventHandlers[event] = [];
    }
    this.eventHandlers[event].push(handler);
  }

  /**
   * 移除事件处理器
   */
  off(event, handler) {
    if (!this.eventHandlers[event]) return;

    if (handler) {
      this.eventHandlers[event] = this.eventHandlers[event].filter(
        (h) => h !== handler
      );
    } else {
      delete this.eventHandlers[event];
    }
  }

  /**
   * 触发事件
   */
  emit(event, data) {
    if (!this.eventHandlers[event]) return;

    this.eventHandlers[event].forEach((handler) => {
      try {
        handler(data);
      } catch (error) {
        console.error(`Error in event handler for ${event}:`, error);
      }
    });
  }
}
